// ============================================================================
// ImportLabsDialog.tsx — импорт анализов CSV в карту пациента.
//
// Простой путь врача: ФАЙЛ → ПРОВЕРКА → РЕЗУЛЬТАТ.
// Сервер (MedicService.ImportLabResultsCsv) сохраняет исходный файл в «Файлах»
// ДО разбора, валидные строки становятся ТЧ «Показатели», ошибки возвращаются
// ПОСТРОЧНО. Исходник остаётся в карточке даже при полном провале разбора —
// поэтому «успеха без данных» не бывает.
// ============================================================================

import { useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Download, FileText, Loader2, Upload, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { fileBase64 } from '@/lib/fileBytes';
import { downloadBytes } from '@/lib/printForm';
import { getSDK } from '@/store/sdkStore';
import { summarizeImport, validateLabFile } from '@/components/medic/patientCardSummary';

interface Props {
    patientId: string;
    patientName?: string;
    onClose: () => void;
    onImported: () => void;
    onOpenResult: (documentId: string) => void;
}

interface RowError {
    line: number;
    message: string;
    raw: string;
}

interface ImportResult {
    documentId: string;
    sourceFileId: string;
    imported: number;
    skipped: number;
    errors: RowError[];
}

type StepState = 'done' | 'active' | 'todo';

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

function Step({ index, label, state }: { index: number; label: string; state: StepState }) {
    const tone = state === 'done'
        ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
        : state === 'active'
            ? 'border-primary/40 bg-primary/10 text-primary'
            : 'border-border bg-muted/30 text-muted-foreground';
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] ${tone}`}>
            <span className="flex h-4 w-4 items-center justify-center rounded-full border text-[10px]">{index}</span>
            {label}
        </span>
    );
}

export function ImportLabsDialog({ patientId, patientName, onClose, onImported, onOpenResult }: Props) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [labName, setLabName] = useState('');
    const [collectedAt, setCollectedAt] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState<ImportResult | null>(null);
    const [downloading, setDownloading] = useState(false);

    const pickFile = (selected: File | null) => {
        setError('');
        setResult(null);
        if (!selected) {
            setFile(null);
            return;
        }
        const problem = validateLabFile(selected);
        if (problem) {
            setFile(null);
            setError(problem);
            return;
        }
        setFile(selected);
    };

    const reset = () => {
        setFile(null);
        setError('');
        setResult(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    const runImport = async () => {
        if (!file || busy) return;
        setBusy(true);
        setError('');
        setResult(null);
        try {
            const buffer = await file.arrayBuffer();
            const response = await getSDK().medic.importLabResultsCsv({
                patientId,
                fileName: file.name,
                data: new Uint8Array(buffer),
                labName: labName.trim(),
                collectedAt,
            });
            const mapped: ImportResult = {
                documentId: String(response.documentId || ''),
                sourceFileId: String(response.sourceFileId || ''),
                imported: Number(response.imported || 0),
                skipped: Number(response.skipped || 0),
                errors: (response.errors || []).map((item) => ({
                    line: Number(item.line || 0),
                    message: String(item.message || ''),
                    raw: String(item.raw || ''),
                })),
            };
            setResult(mapped);
            if (mapped.imported > 0) onImported();
        } catch (importError) {
            logErrorToJournal(importError, `Импорт анализов CSV: пациент ${patientId}`);
            setError(friendlyError(importError));
        } finally {
            setBusy(false);
        }
    };

    const downloadSource = async () => {
        if (!result?.sourceFileId || downloading) return;
        setDownloading(true);
        try {
            const response = await getSDK().files.downloadFile({ id: result.sourceFileId });
            downloadBytes(file?.name || 'analyses.csv', 'text/csv', fileBase64(response.data));
        } catch (downloadError) {
            logErrorToJournal(downloadError, 'Импорт анализов: скачивание исходного файла');
            setError(friendlyError(downloadError));
        } finally {
            setDownloading(false);
        }
    };

    const summary = result
        ? summarizeImport(result.imported, result.skipped, result.errors)
        : null;
    const hasProblems = !!summary && summary.problems > 0;
    const linkFailed = !!result && result.errors.some(
        (item) => item.line <= 0 && /не прикрепл/i.test(item.message),
    );
    const tone = !summary
        ? ''
        : summary.hasData && !hasProblems
            ? 'border-emerald-500/40 bg-emerald-500/10'
            : summary.hasData
                ? 'border-amber-500/40 bg-amber-500/10'
                : 'border-destructive/40 bg-destructive/10';

    return (
        <UniversalWindow
            open
            onClose={onClose}
            title="Импорт анализов из CSV"
            width="w-[min(640px,94vw)]"
            resizable={false}
        >
            <div className="flex h-[min(74vh,660px)] flex-col">
                <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                            Пациент: <span className="text-foreground">{patientName || patientId}</span>
                        </span>
                        <span className="ml-auto flex flex-wrap items-center gap-1.5">
                            <Step index={1} label="Файл" state={file ? 'done' : 'active'} />
                            <Step index={2} label="Проверка" state={busy ? 'active' : result ? 'done' : 'todo'} />
                            <Step index={3} label="Результат" state={result ? 'done' : 'todo'} />
                        </span>
                    </div>

                    <div className="rounded-lg border bg-card p-3">
                        <input
                            ref={inputRef}
                            id="medic-lab-csv-input"
                            type="file"
                            accept=".csv,text/csv"
                            className="sr-only"
                            onChange={(event) => {
                                const selected = event.target.files?.[0] || null;
                                event.target.value = '';
                                pickFile(selected);
                            }}
                        />
                        <div className="flex flex-wrap items-center gap-2">
                            <label
                                htmlFor="medic-lab-csv-input"
                                className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border bg-background px-3 text-sm hover:bg-accent/20"
                            >
                                <Upload className="h-4 w-4" />
                                {file ? 'Выбрать другой файл' : 'Выбрать файл CSV'}
                            </label>
                            <span className="min-w-0 flex-1 text-xs text-muted-foreground">
                                {file ? (
                                    <>
                                        <FileText className="mr-1 inline h-3 w-3" />
                                        <span className="text-foreground">{file.name}</span> · {formatSize(file.size)}
                                    </>
                                ) : 'Файл не выбран'}
                            </span>
                            {file && !busy && (
                                <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={reset}>
                                    Убрать
                                </Button>
                            )}
                        </div>

                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                            <label className="space-y-1 text-xs">
                                <span className="text-muted-foreground">Лаборатория (необязательно)</span>
                                <Input
                                    value={labName}
                                    onChange={(event) => setLabName(event.target.value)}
                                    className="h-9"
                                    placeholder="Например: Инвитро"
                                />
                            </label>
                            <label className="space-y-1 text-xs">
                                <span className="text-muted-foreground">Дата взятия (необязательно)</span>
                                <Input
                                    type="date"
                                    value={collectedAt}
                                    onChange={(event) => setCollectedAt(event.target.value)}
                                    className="h-9"
                                />
                            </label>
                        </div>

                        <details className="mt-2 text-[11px] text-muted-foreground">
                            <summary className="cursor-pointer select-none hover:text-foreground">
                                Как подготовить файл
                            </summary>
                            <div className="mt-1.5 space-y-1 rounded-md border bg-muted/20 p-2">
                                <div>
                                    Обязательные колонки: <span className="text-foreground">Код</span>,{' '}
                                    <span className="text-foreground">Название</span>,{' '}
                                    <span className="text-foreground">Значение</span>.
                                </div>
                                <div>
                                    Необязательные: «Единица» и «Референс» (например, <code>120-160</code>, <code>&lt;5</code>).
                                </div>
                                <div>Разделитель — точка с запятой, запятая или табуляция. Кодировка UTF-8 или Windows-1251.</div>
                                <div>Ошибочные строки не импортируются и показываются с номером строки файла.</div>
                            </div>
                        </details>
                    </div>

                    {error && (
                        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                            <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span className="min-w-0 flex-1 break-words">{error}</span>
                        </div>
                    )}

                    {busy && (
                        <div className="flex items-center gap-2 rounded-md border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Проверяем файл и сохраняем результат…
                        </div>
                    )}

                    {result && summary && (
                        <div className="space-y-2">
                            <div className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${tone}`}>
                                {summary.hasData
                                    ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                                    : <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />}
                                <div className="min-w-0 flex-1 space-y-1">
                                    <div className="font-medium text-foreground">
                                        {summary.hasData
                                            ? `Импортировано показателей: ${summary.imported}`
                                            : 'Ни одна строка не импортирована'}
                                    </div>
                                    <div className="text-muted-foreground">
                                        {summary.rowErrors > 0 && `Строк с ошибками: ${summary.rowErrors}. `}
                                        {summary.fileErrors > 0 && 'Файл не удалось разобрать полностью. '}
                                        {summary.problems === 0 && 'Ошибок нет. '}
                                        {result.sourceFileId
                                            ? linkFailed
                                                ? `Исходный файл «${file?.name || 'analyses.csv'}» сохранён в файлах базы, но не прикреплён к карточке — см. ошибку ниже.`
                                                : `Исходный файл «${file?.name || 'analyses.csv'}» сохранён в карточке пациента.`
                                            : 'Исходный файл сохранить не удалось.'}
                                    </div>
                                    {result.documentId ? (
                                        <div className="text-muted-foreground">Результат добавлен в анализы пациента.</div>
                                    ) : (
                                        <div className="text-muted-foreground">
                                            Документ не создан: исправьте файл и попробуйте снова. Исходник уже в карточке.
                                        </div>
                                    )}
                                    <div className="flex flex-wrap gap-2 pt-1">
                                        {result.documentId && (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className="h-7 text-[11px]"
                                                onClick={() => onOpenResult(result.documentId)}
                                            >
                                                Открыть результат
                                            </Button>
                                        )}
                                        {result.sourceFileId && (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className="h-7 text-[11px]"
                                                onClick={() => void downloadSource()}
                                                disabled={downloading}
                                            >
                                                {downloading
                                                    ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                    : <Download className="mr-1 h-3 w-3" />}
                                                Скачать исходный файл
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {result.errors.length > 0 && (
                                <div className="rounded-md border">
                                    <div className="border-b bg-muted/30 px-2.5 py-1.5 text-[11px] font-medium">
                                        Ошибки по строкам ({result.errors.length})
                                    </div>
                                    <div className="max-h-56 overflow-y-auto">
                                        {result.errors.map((item, index) => (
                                            <div
                                                key={`${item.line}-${index}`}
                                                className="border-b px-2.5 py-1.5 text-[11px] last:border-b-0"
                                            >
                                                <div className="font-medium">
                                                    {item.line > 0 ? `Строка ${item.line}` : 'Файл'}
                                                    {': '}
                                                    <span className="font-normal text-destructive">{item.message}</span>
                                                </div>
                                                {item.raw && (
                                                    <div className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground" title={item.raw}>
                                                        {item.raw}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t p-3">
                    <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={busy}>
                        Закрыть
                    </Button>
                    {result ? (
                        <Button type="button" size="sm" onClick={reset} disabled={busy}>
                            <Upload className="mr-1 h-3.5 w-3.5" />
                            Импортировать ещё
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            size="sm"
                            onClick={() => void runImport()}
                            disabled={!file || busy}
                        >
                            {busy
                                ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                : <Upload className="mr-1 h-3.5 w-3.5" />}
                            Проверить и импортировать
                        </Button>
                    )}
                </div>
            </div>
        </UniversalWindow>
    );
}
